/**
 * Logique, mode « Vérité » : deux formules comparées dans une même table de
 * vérité (contraposée, réciproque, lois de Morgan, négation d'une
 * implication…). Les lignes où elles diffèrent sont marquées ; la conclusion
 * dit si elles sont équivalentes, et ce qu'on en retient.
 */

import { useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { Note } from '../../ui/controls';
import { fr } from './fr';
import { COMPARISONS, compare, evalProp, propText, subformulas, type Prop } from './logic';

const vf = (b: boolean) => (b ? 'V' : 'F');

export function TruthMode({ rank }: { rank: number }) {
  const list = COMPARISONS.filter((c) => (c.minRank ?? 0) <= rank);
  const [id, setId] = useState(list[0]!.id);
  const c = list.find((x) => x.id === id) ?? list[0]!;
  const { vars, table, equivalent } = compare(c);
  const colsL = subformulas(c.left);
  const colsR = subformulas(c.right).filter((p) => !colsL.some((q) => propText(q) === propText(p)));
  const cols: Array<{ p: Prop; final: 'left' | 'right' | null }> = [
    ...colsL.map((p) => ({ p, final: p === colsL[colsL.length - 1] ? ('left' as const) : null })),
    ...colsR.map((p) => ({
      p,
      final: propText(p) === propText(c.right) ? ('right' as const) : null,
    })),
  ];
  // la formule de droite peut déjà figurer à gauche (rare) : on la marque quand même
  if (!cols.some((x) => x.final === 'right')) {
    const k = cols.findIndex((x) => propText(x.p) === propText(c.right));
    if (k >= 0) cols[k] = { ...cols[k]!, final: 'right' };
  }
  const diff = table.filter((r) => r.left !== r.right);
  const isImp = c.left.k === 'imp' && c.left.a.k === 'var' && c.left.b.k === 'var';

  return (
    <div className="space-y-4">
      <div role="group" aria-label="Formules à comparer" className="flex flex-wrap gap-2">
        {list.map((x) => (
          <button
            key={x.id}
            type="button"
            aria-pressed={x.id === c.id}
            onClick={() => setId(x.id)}
            className={cn(
              'min-h-11 rounded-xl border px-3 text-sm font-semibold',
              x.id === c.id
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-200 text-ink-700 hover:bg-ink-50',
            )}
          >
            {fr(x.label)}
          </button>
        ))}
      </div>

      <p className="text-ink-900 text-center text-base font-semibold">
        <span className="text-primary-700">{fr(`« ${propText(c.left)} »`)}</span>
        <span className="text-ink-500 mx-2 text-sm font-normal">comparée à</span>
        <span className="text-accent-700">{fr(`« ${propText(c.right)} »`)}</span>
      </p>

      <div className="border-ink-100 overflow-x-auto rounded-2xl border">
        <table className="w-full border-collapse text-center text-sm" data-testid="lg-table">
          <caption className="sr-only">
            Table de vérité de « {propText(c.left)} » comparée à « {propText(c.right)} »
          </caption>
          <thead>
            <tr className="bg-ink-50">
              {vars.map((v) => (
                <th
                  key={v}
                  scope="col"
                  className="text-ink-800 whitespace-nowrap px-2 py-2 font-bold italic"
                >
                  {v}
                </th>
              ))}
              {cols.map(({ p, final }) => (
                <th
                  key={propText(p)}
                  scope="col"
                  className={cn(
                    'whitespace-nowrap px-2 py-2 font-semibold',
                    final === 'left' && 'text-primary-700',
                    final === 'right' && 'text-accent-700',
                    !final && 'text-ink-500 font-normal',
                  )}
                >
                  {propText(p)}
                </th>
              ))}
              <th scope="col" className="w-8 px-1 py-2">
                <span className="sr-only">Les deux formules diffèrent ?</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {table.map((r, i) => {
              const bad = r.left !== r.right;
              return (
                <tr
                  key={i}
                  className={cn('border-ink-100 border-t', bad && 'bg-error-50')}
                  data-diff={bad ? '' : undefined}
                >
                  {vars.map((v) => (
                    <td key={v} className="text-ink-800 px-2 py-2 font-semibold">
                      {vf(r.env[v])}
                    </td>
                  ))}
                  {cols.map(({ p, final }) => {
                    const val = vf(evalProp(p, r.env));
                    return (
                      <td
                        key={propText(p)}
                        className={cn(
                          'px-2 py-2',
                          final ? 'text-ink-900 font-bold' : 'text-ink-500',
                        )}
                      >
                        {val}
                      </td>
                    );
                  })}
                  <td className="text-error-700 px-1 py-2 font-bold">
                    {bad ? (
                      <>
                        <span aria-hidden>≠</span>
                        <span className="sr-only">diffèrent</span>
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="sr-only" aria-live="polite">
        {equivalent ? 'Équivalentes' : `Pas équivalentes : ${diff.length} lignes diffèrent`}
      </p>
      <Note tone={equivalent ? 'success' : 'error'} live={false}>
        {fr(
          equivalent
            ? `Les deux colonnes coïncident sur toutes les lignes : « ${propText(c.left)} » et « ${propText(c.right)} » sont équivalentes. ${c.lesson}`
            : `Les colonnes diffèrent sur ${diff.length} ligne${diff.length > 1 ? 's' : ''} (en rouge, marquées ≠), par exemple quand ${vars
                .map((v) => `${v} est ${diff[0]!.env[v] ? 'vraie' : 'fausse'}`)
                .join(' et ')} : elles ne sont pas équivalentes. ${c.lesson}`,
        )}
      </Note>

      {isImp && (
        <section
          className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
          data-testid="lg-cncs"
        >
          <p className="text-ink-600 text-sm font-semibold">Lire une implication</p>
          <p className="text-ink-800 text-sm">
            {fr(
              '« P ⇒ Q » se lit « si P, alors Q ». P est une condition SUFFISANTE pour Q ; Q est une condition NÉCESSAIRE pour P. Elle n’est fausse que si P est vraie et Q fausse.',
            )}
          </p>
          <p className="text-ink-600 text-sm">
            {fr(
              'Avec des ensembles : si P est « x ∈ A » et Q est « x ∈ B », alors « pour tout x, P ⇒ Q » veut dire A ⊂ B.',
            )}
          </p>
        </section>
      )}
    </div>
  );
}
